import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import PrizeWonChip from './PrizeWonChip';
import {
  getChallengeAnalytics,
  partitionLabel,
  toParticipant,
} from '../../../utils/incentiveChallengeApi';
import {
  CHALLENGE_TYPE_LABELS,
  formatChallengeValue,
  formatGap,
} from '../../../utils/challengeFormat';

/**
 * Who is in a challenge and how much each of them has done.
 *
 * The two kinds read differently, because they are measured differently:
 *
 *   group   ONE pooled target. The panel leads with the group's running total
 *           against it and a single achieved-or-not verdict, then lists what
 *           each member put in. There is no per-person target to miss, so no
 *           per-person status, no gap column, and no achievers tab — everybody
 *           contributes to the same number.
 *   role    everybody has the SAME target and passes or fails on their own, so
 *           the achievers tab is a real distinction worth having.
 *
 * Both the rows and the verdict come from the analytics endpoint, which applies
 * the same metric definitions the badge and the Targets portal read.
 */
/** "01 Oct", or "01–07 Oct" for a longer round. */
const roundHeading = (r) =>
  r.partition_start === r.partition_end
    ? dayjs(r.partition_start).format('DD MMM')
    : `${dayjs(r.partition_start).format('DD')}–${dayjs(r.partition_end).format('DD MMM')}`;

/**
 * A pooled challenge judged round by round: the group climbs the ladder afresh
 * each round, so the panel is one row per round rather than one total.
 */
function GroupRounds({ block, type }) {
  return (
    <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap">
        <Typography variant="subtitle2">
          {partitionLabel(block.type, block.round_days, block.total_rounds)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {block.rounds_won} of {block.elapsed_rounds} rounds won
        </Typography>
      </Stack>
      <TableContainer sx={{ maxHeight: 220, mt: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Round</TableCell>
              <TableCell align="right">Together</TableCell>
              <TableCell align="right">Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {block.rounds.map((r) => (
              <TableRow key={r.partition_start} hover selected={r.state === 'live'}>
                <TableCell>{roundHeading(r)}</TableCell>
                <TableCell align="right">
                  {r.state === 'upcoming' || r.state === 'absent'
                    ? '—'
                    : formatChallengeValue(r.achieved, type)}
                </TableCell>
                <TableCell align="right">
                  {r.state === 'upcoming' ? (
                    <Typography variant="caption" color="text.disabled">
                      Not started
                    </Typography>
                  ) : r.tier_reached > 0 ? (
                    <PrizeWonChip prize={r.prize} />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {r.state === 'live' ? 'Under way' : 'Missed'}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

GroupRounds.propTypes = {
  block: PropTypes.object.isRequired,
  type: PropTypes.string,
};

export default function ParticipantsDialog({ challenge, open, setOpen }) {
  const [tab, setTab] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !challenge) {
      setAnalytics(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setTab('all');
    setExpanded(null);
    setLoading(true);
    setError(null);
    getChallengeAnalytics(challenge.id)
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 'success') setAnalytics(res.data);
        else setError(res?.message || 'Could not load the participants');
      })
      .catch((err) => !cancelled && setError(err?.message || 'Could not load the participants'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, challenge]);

  const group = analytics?.challenge?.is_group ?? false;
  const summary = analytics?.summary ?? null;

  const rows = useMemo(() => {
    if (!analytics) return [];
    const total = Number(summary?.achieved ?? 0);
    return analytics.participants.map((row) =>
      // A pooled target has one ladder for the whole team, so the rung being
      // chased is the same for everybody and needs no per-person choice.
      toParticipant(row, { isGroup: group, target: Number(summary?.against ?? 0), total }),
    );
  }, [analytics, group, summary]);

  const achievers = useMemo(() => rows.filter((r) => r.status === 'achieved'), [rows]);

  if (!challenge) return null;

  const visible = !group && tab === 'achievers' ? achievers : rows;

  // expander + # + name + role + achieved + progress, and gap when there is a
  // personal target to miss.
  const columnCount = 6 + (group ? 0 : 1);

  return (
    <GlobalDialog
      maxWidth="md"
      fullWidth
      title={challenge.name}
      open={open}
      setOpen={setOpen}
      closeButton
      closeCondition={() => setOpen(false)}
      content={
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {CHALLENGE_TYPE_LABELS[challenge.type] || challenge.type} · {challenge.scopeLabel} ·{' '}
            {challenge.assignmentLabel}
          </Typography>

          {loading && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {!loading && !error && summary && (
            <>
          {group && summary.partition ? (
            <GroupRounds block={summary.partition} type={challenge.type} />
          ) : group ? (
            // One number for the whole group, and one verdict on it.
            <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                flexWrap="wrap"
                spacing={1}
              >
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography variant="h6" fontWeight={700}>
                    {formatChallengeValue(summary.achieved, challenge.type)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of {formatChallengeValue(summary.against ?? 0, challenge.type)} together
                  </Typography>
                </Stack>

                {summary.team_achieved ? (
                  <Chip size="small" color="success" label="Target achieved" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {formatGap(summary.gap, challenge.type)}
                  </Typography>
                )}
              </Stack>

              <LinearProgress
                variant="determinate"
                value={Math.min(100, summary.percent)}
                color={summary.team_achieved ? 'success' : 'primary'}
                sx={{ mt: 1.5, height: 8, borderRadius: 999 }}
              />

              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                {summary.percent}% of the target · {summary.participants} members contributing
              </Typography>
            </Box>
          ) : (
            <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 1 }}>
              <Tab value="all" label={`All (${rows.length})`} />
              <Tab
                value="achievers"
                label={`Achievers (${achievers.length})`}
              />
            </Tabs>
          )}

          {group && (
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              What each member put in
            </Typography>
          )}

          {visible.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {tab === 'achievers' && !group
                  ? 'Nobody has reached the target yet.'
                  : 'No participants resolved for this challenge.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ width: 48 }}>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell align="right">{group ? 'Contributed' : 'Achieved'}</TableCell>
                    <TableCell sx={{ width: 170 }}>
                      {group ? 'Share of the total' : 'Progress'}
                    </TableCell>
                    {!group && <TableCell align="right">Gap</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map((row, index) => (
                    <Fragment key={row.userId}>
                    <TableRow hover>
                      <TableCell sx={{ py: 0.25 }}>
                        {row.members?.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => setExpanded(expanded === row.userId ? null : row.userId)}
                            aria-label={expanded === row.userId ? 'Hide team' : 'Show team'}
                          >
                            <Icon
                              icon={expanded === row.userId ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                              width={18}
                            />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{row.role}</TableCell>
                      <TableCell align="right">
                        {formatChallengeValue(row.achieved, challenge.type)}
                      </TableCell>
                      <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, group ? row.share : row.percent)}
                              color={!group && row.status === 'achieved' ? 'success' : 'primary'}
                              sx={{ flexGrow: 1, height: 6, borderRadius: 999 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 34 }}>
                              {group ? row.share : row.percent}%
                            </Typography>
                          </Stack>
                        </TableCell>
                      {!group && (
                        <TableCell align="right">
                          {row.status === 'achieved' ? (
                            <Chip size="small" color="success" label="Achieved" />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {formatGap(row.gap, challenge.type)}
                            </Typography>
                          )}
                        </TableCell>
                      )}
                    </TableRow>

                    {/* A head records no payments themselves, so their figure is
                        the sum of the people under them — shown here rather than
                        left as a lump sum nobody can account for. */}
                    {row.members?.length > 1 && (
                      <TableRow>
                        <TableCell sx={{ py: 0, border: 0 }} colSpan={columnCount}>
                          <Collapse in={expanded === row.userId} unmountOnExit>
                            <Box sx={{ my: 1, ml: 5, mr: 1, p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {row.name}&apos;s team — {row.members.length} people
                              </Typography>
                              <Table size="small" sx={{ mt: 0.5 }}>
                                <TableBody>
                                  {row.members.map((m) => (
                                    <TableRow key={m.userId}>
                                      <TableCell sx={{ border: 0, py: 0.4 }}>
                                        {m.name}
                                        {m.isHead && (
                                          <Typography
                                            component="span"
                                            variant="caption"
                                            sx={{ ml: 0.75, color: 'text.disabled' }}
                                          >
                                            (self)
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ border: 0, py: 0.4, color: 'text.secondary' }}>
                                        {m.role}
                                      </TableCell>
                                      <TableCell align="right" sx={{ border: 0, py: 0.4 }}>
                                        {formatChallengeValue(m.achieved, challenge.type)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            </>
          )}
        </Box>
      }
    />
  );
}

ParticipantsDialog.propTypes = {
  challenge: PropTypes.object,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};
